import { date, getCurrentYear } from '../utils';
import {
    ENERGY,
    SECURITY,
} from '../utils/ProductTypes';

export const AUCTION_NAME_LENGTH = 50;
export const auctionNameValidator = (t) => (input) => {
    let err = null;
    const maxLetters = AUCTION_NAME_LENGTH;
    if (input.trim().length === 0) {
        err = t('auctions.validation.auction-name-empty');
    } else if (input.length > maxLetters) {
        err = t('auctions.validation.auction-name-too-long', { maxLetters });
    }
    return err;
};

export const auctionDeliveryYearValidator = (t) => (input) => {
    let err = null;
    const parsedInput = parseInt(input, 10);
    if (input.trim().length === 0) {
        err = t('auctions.validation.auction-delivery-year-empty');
    } else if (!input.match(/^\d+$/)) {
        err = t('auctions.validation.auction-delivery-year-not-a-number');
    } else if (parsedInput < getCurrentYear()) {
        err = t('auctions.validation.auction-delivery-year-in-past');
    }
    return err;
};

export const auctionQuantityValueValidator = (t, productType) => (input) => {
    let err = null;
    if (productType.is(ENERGY)) {
        err = auctionNumberOfLotsValidator(t)(input);
    } else if (productType.is(SECURITY)) {
        err = auctionQuantityValidator(t)(input);
    }
    return err;
};

export const auctionNumberOfLotsValidator = (t) => (input) => {
    let err = null;
    const parsedInput = parseInt(input, 10);
    if (input.trim().length === 0) {
        err = t('auctions.validation.auction-number-of-lots-empty');
    } else if (!input.match(/^\d+$/)) {
        err = t('auctions.validation.auction-number-of-lots-not-positive-number');
    } else if (parsedInput < 1) {
        err = t('auctions.validation.auction-number-of-lots-not-positive-number');
    }
    return err;
};

export const auctionQuantityValidator = (t) => (input) => {
    let err = null;
    const parsedInput = parseInt(input, 10);
    if (input.trim().length === 0) {
        err = t('auctions.validation.auction-quantity-empty');
    } else if (!input.match(/^\d+$/) || parsedInput < 1) {
        err = t('auctions.validation.auction-quantity-not-positive-number');
    }
    return err;
};

export const auctionPowerValidator = (t) => (input) => {
    let err = null;
    const parsedInput = parseInt(input, 10);
    if (input.trim().length === 0) {
        err = t('auctions.validation.auction-power-empty');
    } else if (!input.match(/^\d+$/)) {
        err = t('auctions.validation.auction-power-not-positive-number');
    } else if (parsedInput < 1) {
        err = t('auctions.validation.auction-power-not-positive-number');
    }
    return err;
};

export const auctionDateValidator = (t) => (input) => {
    let err = null;
    if (!input) {
        err = t('auctions.validation.auction-date-empty');
    } else if (!(input instanceof Date)) {
        err = t('auctions.validation.auction-date-not-a-date');
    } else if (input < date()) {
        err = t('auctions.validation.auction-time-not-in-past');
    }
    return err;
};

export const auctionSmallestQuantityUnitValidator = (t, minSmallestUnit, quantity, maxBidSize) => (input) => {
    let err = null;
    const parsedInput = parseInt(input, 10);
    const parsedQuantity = parseInt(quantity, 10);
    const parsedMaxBidSize = parseInt(maxBidSize, 10);
    let maxSmallestUnit = null;
    let valueGreaterThanSmallestErrMessage = null;
    if (parsedQuantity && parsedMaxBidSize) {
        if (parsedQuantity > parsedMaxBidSize) {
            maxSmallestUnit = parsedMaxBidSize;
            valueGreaterThanSmallestErrMessage = 'auction-smallest-quantity-unit-greater-than-max-bid-size';
        } else {
            maxSmallestUnit = parsedQuantity;
            valueGreaterThanSmallestErrMessage = 'auction-smallest-quantity-unit-greater-than-quantity';
        }
    } else if (parsedQuantity) {
        maxSmallestUnit = parsedQuantity;
        valueGreaterThanSmallestErrMessage = 'auction-smallest-quantity-unit-greater-than-quantity';
    } else if (parsedMaxBidSize) {
        maxSmallestUnit = parsedMaxBidSize;
        valueGreaterThanSmallestErrMessage = 'auction-smallest-quantity-unit-greater-than-max-bid-size';
    }
    if (!input || input.trim().length === 0) {
        err = t('auctions.validation.auction-smallest-quantity-unit-empty');
    } else if (!input.match(/^\d+$/)) {
        err = t('auctions.validation.auction-smallest-quantity-unit-invalid-integer');
    } else if (parsedInput < minSmallestUnit) {
        err = t('auctions.validation.auction-smallest-quantity-unit-lower-than-min', { minSmallestUnit });
    } else if (maxSmallestUnit && parsedInput > maxSmallestUnit) {
        err = t(`auctions.validation.${valueGreaterThanSmallestErrMessage}`);
    }
    return err;
};

export const auctionMaxBidSizeValidator = (t, staticMinMaxBidSize, smallestQuantityUnit, quantity) => (input) => {
    let err = null;
    const parsedInput = parseInt(input, 10);
    const parsedSmallestQuantityUnit = parseInt(smallestQuantityUnit, 10);
    const parsedQuantity = parseInt(quantity, 10);
    if (parsedInput && !input.match(/^\d+$/)) {
        err = t('auctions.validation.auction-maximum-bid-size-invalid-integer');
    } else if (parsedSmallestQuantityUnit && parsedInput < parsedSmallestQuantityUnit) {
        err = t('auctions.validation.auction-maximum-bid-size-lower-than-smallest-unit');
    } else if (!parsedSmallestQuantityUnit && parsedInput < staticMinMaxBidSize) {
        err = t('auctions.validation.auction-maximum-bid-size-lower-than-min', { minMaximumBidSize: staticMinMaxBidSize });
    } else if (parsedQuantity && parsedInput > parsedQuantity) {
        err = t('auctions.validation.auction-maximum-bid-size-greater-than-quantity');
    }
    return err;
};

const priceFormatValidator = (t) => (input) => {
    let err = null;
    const parsedInput = parseFloat(input);
    if (parsedInput && !input.match(/^-?\d+(\.\d{1,2})?$/)) {
        err = t('auctions.validation.auction-price-invalid-number');
    }
    return err;
};

export const auctionMinPriceValidator = (t, maxPrice) => (input) => {
    let err = priceFormatValidator(t)(input);
    const parsedInput = parseFloat(input);
    if (err === null && maxPrice !== null && parsedInput > maxPrice) {
        err = t('auctions.validation.auction-min-price-greater-than-max');
    }
    return err;
};

export const auctionMaxPriceValidator = (t, minPrice) => (input) => {
    let err = priceFormatValidator(t)(input);
    const parsedInput = parseFloat(input);
    if (err === null && minPrice !== null && parsedInput < minPrice) {
        err = t('auctions.validation.auction-max-price-lower-than-min');
    }
    return err;
};

export const AUCTION_DESCRIPTION_MAX_LENGTH = 255;
export const auctionDescriptionValidator = (t) => (input) => {
    let err = null;
    if (input && input.length > AUCTION_DESCRIPTION_MAX_LENGTH) {
        err = t('auctions.validation.auction-description-too-long', { maxLetters: AUCTION_DESCRIPTION_MAX_LENGTH });
    }
    return err;
};

export const auctionSpotReferencePriceValidator = (t, minSpotReferencePrice, maxSpotReferencePrice) => (input) => {
    let err = priceFormatValidator(t)(input);
    if (err === null) {
        const parsedInput = parseFloat(input);
        if (parsedInput < minSpotReferencePrice) {
            err = t('auctions.validation.auction-spot-reference-price-lower-than-min', { minSpotReferencePrice });
        } else if (parsedInput > maxSpotReferencePrice) {
            err = t('auctions.validation.auction-spot-reference-price-higher-than-max', { maxSpotReferencePrice });
        }
    }
    return err;
};

export const auctionAbsoluteDeviationValidator = (t, minAbsoluteDeviation, maxAbsoluteDeviation) => (input) => {
    let err = priceFormatValidator(t)(input);
    if (err === null) {
        const parsedInput = parseFloat(input);
        if (parsedInput < minAbsoluteDeviation) {
            err = t('auctions.validation.auction-absolute-deviation-lower-than-min', { minAbsoluteDeviation });
        } else if (parsedInput > maxAbsoluteDeviation) {
            err = t('auctions.validation.auction-absolute-deviation-higher-than-max', { maxAbsoluteDeviation });
        }
    }
    return err;
};

export const auctionRelativeDeviationValidator = (t, minRelativeDeviation, maxRelativeDeviation) => (input) => {
    let err = null;
    const parsedInput = parseFloat(input);
    if (parsedInput && !input.match(/^-?\d+(\.\d{1,2})?$/)) {
        err = t('auctions.validation.relative-deviation-invalid-number');
    } else if (parsedInput < minRelativeDeviation) {
        err = t('auctions.validation.auction-relative-deviation-lower-than-min', { minRelativeDeviation });
    } else if (parsedInput > maxRelativeDeviation) {
        err = t('auctions.validation.auction-relative-deviation-higher-than-max', { maxRelativeDeviation });
    }
    return err;
};

export const auctionAuctioneerDataItemsValidator = (t, quantity) => (input) => {
    let err = null;
    if (!input.length) {
        err = t('auctions.validation.auction-auctioneer-data-items-empty');
    } else {
        const quantitySum = input.reduce((a, currentValue) => a + currentValue.quantity, 0);
        if (quantitySum !== quantity) {
            err = t('auctions.validation.auction-auctioneer-data-items-sum-quantity-not-equal', { quantitySum, quantity });
        }
    }
    return err;
};

export const auctionAuctioneerDataTemplateItemsValidator = (t, quantity) => (input) => {
    let err = null;
    if (input?.length) {
        const quantitySum = input.reduce((a, currentValue) => a + currentValue.quantity, 0);
        if (quantitySum !== quantity) {
            err = t('auctions.validation.auction-auctioneer-data-template-items-sum-quantity-not-equal', { quantitySum, quantity });
        }
    }
    return err;
};
